"use client";

import React from "react";
import { Cpu, Network, ShieldCheck, Zap, ArrowRight, Settings, Database, Activity } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SteuerungstechnikSps() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const hardFacts = [{
    feature: "Steuerungsplattform",
    spec: "WAGO Controller & I/O Systeme"
  }, {
    feature: "Programmierung",
    spec: "Codesys basierte SPS Programmierung"
  }, {
    feature: "Kommunikation",
    spec: "Modbus, TCP/IP, MQTT"
  }, {
    feature: "Integration",
    spec: "Maschinen, technische Anlagen und Sonderlösungen"
  }];
  return <section id="steuerungstechnik-sps" className="dark bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: `linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)`,
      backgroundSize: '40px 40px'
    }} />
      
      {}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_30%_30%,color-mix(in_srgb,var(--color-primary),transparent_92%),transparent_70%)]" />

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-mono tracking-wider uppercase">
                <Cpu className="w-3 h-3" />
                <span>Industrial Grade Automation</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">Industrielle Steuerungstechnik</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">Neben der Gebäudeautomation realisieren wir auch Steuerungslösungen für industrielle Anlagen und technische Systeme.</p>
              <p className="text-muted-foreground leading-relaxed">Für industrielle Anwendungen setzen wir auf offene und zuverlässige WAGO-Steuerungssysteme. Damit lassen sich Maschinen, technische Anlagen und Prozesse flexibel automatisieren und überwachen. Dieser Bereich ergänzt unsere Kompetenzen in der Systemintegration und ermöglicht auch individuelle Automationslösungen ausserhalb der Gebäudeautomation.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[{
              icon: Network,
              title: "Offene Systeme",
              desc: "Integration über Standardprotokolle wie Modbus, TCP/IP oder MQTT."
            }, {
              icon: ShieldCheck,
              title: "Zuverlässige Steuerung",
              desc: "Robuste Industriehardware für langfristigen Betrieb."
            }, {
              icon: Zap,
              title: "Flexible Programmierung",
              desc: "Individuelle Steuerungslogik mit modernen Automationsplattformen."
            }, {
              icon: Settings,
              title: "Fernzugriff & Diagnose",
              desc: "Überwachung und Wartung von Anlagen aus der Ferne."
            }].map((item, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                  {}
                  <div className="mt-1 p-2 rounded bg-secondary/50 text-primary border border-primary/10">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              {}
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_color-mix(in_srgb,var(--color-primary),transparent_70%)]">
                <Link to="/ContactPage">
                  Beratung anfordern <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-transparent rounded-lg blur opacity-20" />
            
            <div className="relative bg-card/50 backdrop-blur-sm border border-border rounded-lg overflow-hidden shadow-2xl">
              <div className="px-6 py-4 border-b border-border bg-muted/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />
                  <span className="font-mono text-sm font-bold uppercase tracking-wider text-foreground">System Spezifikationen</span>
                </div>
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500/50" />
                  <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
                  <div className="w-2 h-2 rounded-full bg-green-500/50" />
                </div>
              </div>

              <div className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead className="w-[180px] text-primary font-semibold">Merkmal</TableHead>
                      <TableHead className="text-foreground font-semibold">Technische Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hardFacts.map((fact, i) => <TableRow key={i} data-index={i} className="border-border hover:bg-primary/5 transition-colors">
                        <TableCell className="font-medium text-muted-foreground font-mono text-xs uppercase tracking-wide">
                          {fact.feature}
                        </TableCell>
                        <TableCell className="text-foreground font-mono text-sm">
                          {fact.spec}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>

              <div className="px-6 py-3 border-t border-border bg-muted/20 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <div className="flex items-center gap-2">
                  <Database className="w-3 h-3" />
                  <span>STATUS: ONLINE</span>
                </div>
                <span>S2 AUTOMATION</span>
              </div>
            </div>

            {}
            
          </div>

        </div>
      </div>
    </section>;
}